<template>
  <v-dialog :model-value="dialog" @update:model-value="updateDialog" fullscreen persistent
    transition="dialog-bottom-transition">
    <v-card>
      <!-- Header -->
      <v-toolbar flat color="primary">
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="white--text">{{ formTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text class="white--text" @click="guardarCotizacion" :disabled="itemsCotizados.length === 0">
          Guardar Cotización
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form ref="form">
          <v-container>
            <!-- Cliente -->
            <v-row>
              <v-col cols="3">
                <v-text-field v-model="dni" label="DNI" density="compact" outlined append-icon="mdi-magnify"
                  @click:append="buscarPorDni" @keyup.enter="buscarPorDni" />
              </v-col>
              <v-col cols="4">
                <v-text-field v-model="nombres" label="Nombres" :rules="[rules.required]" density="compact" outlined />
              </v-col>
              <v-col cols="3">
                <v-text-field v-model="celular" label="Celular" density="compact" outlined />
              </v-col>
              <v-col cols="2">
                <v-alert color="primary" variant="tonal" class="text-center">
                  <span class="text-h4"><strong>Total: S/ {{ total }}</strong></span>
                </v-alert>
              </v-col>

            </v-row>

            <!-- Agregar ítems -->
            <v-row>
              <v-col cols="4">
                <v-select v-model="selectedCurso" :items="cursos" item-title="descripcion" item-value="id"
                  density="compact" label="Selecciona un curso">
                  <template #append>
                    <v-icon icon="mdi-plus-circle-outline" color="primary" @click="agregarItem('curso')" />
                  </template>
                </v-select>
              </v-col>

              <v-col cols="4">
                <v-select v-model="selectedAlquiler" :items="alquileres" item-value="id" density="compact"
                  label="Selecciona un alquiler">
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props" :subtitle="item.raw.auto.marca" :title="item.raw.auto.modelo + ' ' +
                      item.raw.auto.color + ' ' +
                      item.raw.auto.placa" />
                  </template>
                  <template #selection="{ item }">
                    <span>{{ item.raw.auto.placa }}</span>
                  </template>
                  <template #append>
                    <v-icon icon="mdi-plus-circle-outline" color="primary" @click="agregarItem('alquiler')" />
                  </template>
                </v-select>
              </v-col>

              <v-col cols="4">
                <v-select v-model="selectedServicioExterno" :items="servicioExternos" item-title="descripcion"
                  item-value="id" density="compact" label="Selecciona un servicio externo">
                  <template #append>
                    <v-icon icon="mdi-plus-circle-outline" color="primary" @click="agregarItem('servicio externo')" />
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Condiciones -->
            <v-row>
              <v-col cols="6">
                <v-textarea v-model="condiciones_pago" label="Condiciones de Pago" auto-grow outlined />
              </v-col>
              <v-col cols="6">
                <v-textarea v-model="notas" label="Notas" auto-grow outlined />
              </v-col>
            </v-row>

            <!-- Tabla de ítems -->
            <v-row>
              <v-col cols="12">
                <v-data-table :headers="headers" :items="itemsCotizados" item-value="claveUnica" hide-default-footer
                  :items-per-page="-1">

                  <!-- Tipo -->
                  <template v-slot:[`item.tipo`]="{ item }">
                    {{ item.tipo === 'curso'
                      ? 'Curso'
                      : item.tipo === 'alquiler'
                        ? 'Alquiler'
                        : 'Servicio Externo' }}
                  </template>

                  <!-- Cantidad -->
                  <template v-slot:[`item.cantidad`]="{ item }">
                    <v-text-field v-model.number="item.cantidad" type="number" min="1" :error="item.cantidad <= 0"
                      :error-messages="item.cantidad <= 0
                        ? 'Cantidad debe ser mayor que 0' : ''" @change="calcularTotal" />
                  </template>

                  <!-- Precio -->
                  <template v-slot:[`item.precio`]="{ item }">
                    <v-text-field v-model.number="item.precio" type="number" min="0.01" prefix="S/ "
                      :error="item.precio <= 0" :error-messages="item.precio <= 0
                        ? 'Precio debe ser mayor que 0' : ''" @change="calcularTotal" />
                  </template>

                  <!-- Subtotal -->
                  <template v-slot:[`item.subtotal`]="{ item }">
                    <span>S/ {{ (Number(item.cantidad) * Number(item.precio)).toFixed(2) }}</span>
                  </template>

                  <!-- Acciones -->
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-icon color="red" @click="eliminarItem(item)">mdi-delete</v-icon>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    dialog: Boolean,
    editedItem: Object,
    formTitle: String,
  },
  data() {
    return {
      selectedCurso: null,
      selectedAlquiler: null,
      selectedServicioExterno: null,
      itemsCotizados: [],
      total: 0,
      dni: '',
      nombres: '',
      celular: '',
      condiciones_pago: '',
      notas: '',
      rules: {
        required: v => !!v || 'El campo Nombre es requerido',
      },
      headers: [
        { title: 'Tipo', value: 'tipo' },
        { title: 'Descripción', value: 'descripcion' },
        { title: 'Cantidad', value: 'cantidad', sortable: false },
        { title: 'Precio Unitario', value: 'precio', sortable: false },
        { title: 'Subtotal', value: 'subtotal', sortable: false },
        { title: 'Acciones', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters('curso', ['cursos']),
    ...mapGetters('alquiler', ['alquileres']),
    ...mapGetters('servicioexterno', ['servicioExternos']),
  },
  watch: {
    dialog(val) {
      if (val && !this.editedItem?.id) this.resetForm();
    },
    editedItem: {
      immediate: true,
      deep: true,
      handler(item) {
        if (!item?.id) return;
        this.dni = item.numero_documento || '';
        this.nombres = item.nombre_cliente || '';
        this.celular = item.celular || '';
        this.condiciones_pago = item.condiciones_pago || '';
        this.notas = item.notas || '';
        this.itemsCotizados = (item.items || []).map(i => ({
          ...i,
          cantidad: i.cantidad || 1,
          precio: i.precio || 0,
          claveUnica: `${i.tipo}_${i.id}`,
        }));
        this.calcularTotal();
      },
    },
    itemsCotizados: {
      deep: true,
      handler() { this.calcularTotal(); },
    },
  },
  created() {
    this.fetchCursos();
    this.fetchAlquileres();
    this.fetchServicioExternos();
  },
  methods: {
    ...mapActions('curso', ['fetchCursos']),
    ...mapActions('alquiler', ['fetchAlquileres']),
    ...mapActions('servicioexterno', ['fetchServicioExternos']),
    ...mapActions('alumno', ['buscarAlumnoPorDni']),
    ...mapActions('cotizacion', {
      guardarCotizacionAction: 'guardarCotizacion',
      updateCotizacion: 'updateCotizacion',
      fetchCotizaciones: 'fetchCotizaciones',
    }),

    /** diálogo */
    updateDialog(v) { this.$emit('update:dialog', v); },
    close() { this.$emit('update:dialog', false); this.resetForm(); },
    resetForm() {
      this.selectedCurso = null;
      this.selectedAlquiler = null;
      this.selectedServicioExterno = null;
      this.itemsCotizados = [];
      this.total = 0;
      this.dni = '';
      this.nombres = '';
      this.celular = '';
      this.condiciones_pago = '';
      this.notas = '';
    },

    /** cliente */
    buscarPorDni() {
      if (!this.dni) return;
      this.buscarAlumnoPorDni(this.dni)
        .then(({ success, data }) => {
          if (success) {
            this.nombres = `${data.nombre || ''} ${data.apellido_paterno || ''} ${data.apellido_materno || ''}`.trim();
          } else console.warn('No se encontraron datos para este DNI');
        })
        .catch(e => console.error('Error buscando DNI:', e));
    },

    /** ítems */
    agregarItem(tipo) {
      let item = null;
      if (tipo === 'curso' && this.selectedCurso)
        item = this.cursos.find(c => c.id === this.selectedCurso);
      else if (tipo === 'alquiler' && this.selectedAlquiler)
        item = this.alquileres.find(a => a.id === this.selectedAlquiler);
      else if (tipo === 'servicio externo' && this.selectedServicioExterno)
        item = this.servicioExternos.find(s => s.id === this.selectedServicioExterno);
      if (!item) return;

      if (!this.itemsCotizados.some(i => i.id === item.id && i.tipo === tipo)) {
        this.itemsCotizados.push({
          id: item.id,
          tipo,
          descripcion: tipo === 'alquiler'
            ? `${item.auto.marca} ${item.auto.modelo}`
            : item.descripcion,
          cantidad: 1,
          precio: parseFloat(item.precio?.[item.precio.length - 1]?.precio || 0),
          claveUnica: `${tipo}_${item.id}`,
        });
      }

      if (tipo === 'curso') this.selectedCurso = null
      else if (tipo === 'alquiler') this.selectedAlquiler = null
      else if (tipo === 'servicio externo') this.selectedServicioExterno = null

      this.calcularTotal();
    },
    eliminarItem(it) {
      this.itemsCotizados = this.itemsCotizados.filter(i => !(i.id === it.id && i.tipo === it.tipo));
      this.calcularTotal();
    },

    /** totales */
    calcularTotal() {
      this.total = this.itemsCotizados.reduce(
        (s, i) => s + (Number(i.cantidad) || 0) * (Number(i.precio) || 0),
        0,
      ).toFixed(2);
    },

    /** guardar */
    async guardarCotizacion() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;
      try {
        const p = {
          id: this.editedItem?.id,
          dni: this.dni,
          nombres: this.nombres,
          celular: this.celular,
          condiciones_pago: this.condiciones_pago,
          notas: this.notas,
          items: this.itemsCotizados,
          total: this.total,
        };
        if (p.id) await this.updateCotizacion(p);
        else await this.guardarCotizacionAction(p);

        await this.fetchCotizaciones();
        this.close();
      } catch (e) {
        console.error('❌ Error al guardar cotización:', e);
      }
    },
  },
};
</script>
